/**
 * Connectors — letting another product act for you in taskDojo.
 *
 * ## Which way round this goes, and why it changed
 *
 * The first version pushed copies of your tasks into Projectly, because
 * Projectly had no way to ask taskDojo for anything. That meant two stores of
 * the same data, drifting whenever a push failed, and no way to change a task
 * from the other side.
 *
 * It works the other way now. taskDojo issues a token; Projectly holds it and
 * reads the real task whenever it needs one, and writes back through the same
 * API. There is no copy to go stale, and "what does Projectly see" has the
 * only honest answer — exactly what is in taskDojo right now.
 *
 * ## What this file can and cannot tell you
 *
 * taskDojo is the provider here, so there is nothing for it to "test": it
 * cannot call itself and learn anything. What it does know is real and more
 * useful — whether a token exists, and whether anyone has used it. A token
 * that has never been called has not been pasted into Projectly yet, and
 * saying so is more helpful than a green tick that means nothing.
 */

import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebaseConfig';

/**
 * What the owner may see about their own token.
 *
 * Never the token itself. It exists in two places only — your clipboard, and
 * the one response that created it — and nothing on the server can get it
 * back. Losing it means generating another, which is the right trade for a
 * credential that can read and change your tasks.
 */
export interface ConnectorStatus {
  tokenPrefix?: string;
  tokenName?: string;
  /** How many times Projectly has called us with this token. */
  callCount?: number;
  lastUsedAt?: { seconds: number } | string | null;
  createdAt?: { seconds: number } | string | null;
}

const statusRef = (uid: string) => doc(db, 'users', uid, 'integrations', 'projectly');

export const loadStatus = async (uid: string): Promise<ConnectorStatus | null> => {
  if (!uid) return null;
  const snap = await getDoc(statusRef(uid));
  return snap.exists() ? (snap.data() as ConnectorStatus) : null;
};

/**
 * Live, because the interesting moment is the first call.
 *
 * You generate a token here, paste it into Projectly, and want to know it
 * landed. Polling would make that feel broken for however long the interval
 * is; a listener turns it into the thing happening in front of you.
 */
export const watchStatus = (
  uid: string,
  onChange: (status: ConnectorStatus | null) => void,
): (() => void) => {
  if (!uid) return () => {};
  return onSnapshot(
    statusRef(uid),
    (snap) => onChange(snap.exists() ? (snap.data() as ConnectorStatus) : null),
    () => onChange(null),
  );
};

/** Issue a token. The raw value comes back once and is never retrievable. */
export const generateToken = async (name = 'Projectly'): Promise<string> => {
  const call = httpsCallable<{ name: string }, { token: string; prefix: string }>(
    functions, 'generateApiToken',
  );
  const res = await call({ name });
  return res.data.token;
};

/** Revoke every live token and drop the connection. */
export const revokeToken = async (): Promise<number> => {
  const call = httpsCallable<Record<string, never>, { revoked: number }>(
    functions, 'revokeApiToken',
  );
  const res = await call({});
  return res.data.revoked;
};

/** Timestamps arrive either as Firestore objects or ISO strings. */
export const asDate = (v: ConnectorStatus['lastUsedAt']): Date | null => {
  if (!v) return null;
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'object' && 'seconds' in v) return new Date(v.seconds * 1000);
  return null;
};
